import React from 'react';

/**
 * WEB-693: прозрачные инлайн-SVG значки доверия — вместо текстовых чипов на
 * белом фоне. Стилизованные силуэты (не точные логотипы брендов): щиты,
 * замок, карты, кошелёк. Рисуются currentColor/фирменными оттенками на
 * прозрачном фоне, масштабируются от height.
 *
 * Ключи = значения TRUST_BADGE_OPTIONS (BlockPropertiesPanelExtended).
 * Неизвестный ключ → generic-щит (значок доверия по умолчанию).
 */

interface BadgeIconProps {
  badge: string;
  height?: number;
}

const LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  ssl: 'SSL Secure',
  norton: 'Norton',
  mcafee: 'McAfee',
  visa: 'Visa',
  mastercard: 'Mastercard',
  bbb: 'BBB',
};

function ShieldPath({ fill }: { fill: string }): React.ReactElement {
  return <path d="M16 2 L28 7 V16 C28 23.5 23 28.5 16 30 C9 28.5 4 23.5 4 16 V7 Z" fill={fill} />;
}

export function TrustBadgeIcon({ badge, height = 28 }: BadgeIconProps): React.ReactElement {
  const common = {
    'data-testid': `trust-badge-icon-${badge}`,
    height,
    viewBox: '0 0 32 32',
    fill: 'none',
    'aria-label': LABELS[badge] ?? badge,
    role: 'img' as const,
  };

  switch (badge) {
    case 'ssl':
      // Щит + замок
      return (
        <svg {...common}>
          <ShieldPath fill="#4b5563" />
          <rect x="11" y="14" width="10" height="8" rx="1.5" fill="#fff" />
          <path d="M13 14 v-2.5 a3 3 0 0 1 6 0 V14" stroke="#fff" strokeWidth="2" fill="none" />
          <circle cx="16" cy="18" r="1.4" fill="#4b5563" />
        </svg>
      );
    case 'norton':
      // Жёлтый круг-щит с галкой
      return (
        <svg {...common}>
          <circle cx="16" cy="16" r="13" fill="#f2b700" />
          <circle cx="16" cy="16" r="10" fill="#0f172a" />
          <path d="M11 16.5 L14.5 20 L21 12.5" stroke="#f2b700" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    case 'mcafee':
      // Красный щит с «M»
      return (
        <svg {...common}>
          <ShieldPath fill="#c01818" />
          <path d="M10 21 V11 L16 15.5 L22 11 V21" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    case 'visa':
      // Синяя стилизованная «VISA»-плашка (прозрачный фон, только текст)
      return (
        <svg {...common} viewBox="0 0 48 32" width={(height / 32) * 48}>
          <text x="24" y="21" textAnchor="middle" fontSize="13" fontStyle="italic" fontWeight="800" fill="#1a1f71" fontFamily="Helvetica, Arial, sans-serif">
            VISA
          </text>
          <path d="M8 24 H40" stroke="#f7b600" strokeWidth="2" />
        </svg>
      );
    case 'mastercard':
      // Два пересекающихся круга
      return (
        <svg {...common} viewBox="0 0 48 32" width={(height / 32) * 48}>
          <circle cx="19" cy="16" r="10" fill="#eb001b" />
          <circle cx="29" cy="16" r="10" fill="#f79e1b" fillOpacity="0.9" />
        </svg>
      );
    case 'bbb':
      // Синий бейдж-«пламя» BBB
      return (
        <svg {...common}>
          <rect x="4" y="8" width="24" height="16" rx="3" fill="#005a78" />
          <text x="16" y="19.5" textAnchor="middle" fontSize="9" fontWeight="800" fill="#fff" fontFamily="Helvetica, Arial, sans-serif">
            BBB
          </text>
        </svg>
      );
    case 'apple_pay':
      // Стилизованный кошелёк с ябл.-силуэтом
      return (
        <svg {...common} viewBox="0 0 48 32" width={(height / 32) * 48}>
          <rect x="2" y="4" width="44" height="24" rx="6" fill="#0f172a" />
          <circle cx="17" cy="15" r="1.6" fill="#fff" />
          <path d="M17 17 c-2.6 0 -4 2 -4 4.5 c0 1.6 1 3.5 2.3 3.5 c0.8 0 1 -0.5 1.7 -0.5 s0.9 0.5 1.7 0.5 c1.3 0 2.3 -1.9 2.3 -3.5 C21 19 19.6 17 17 17 Z" fill="#fff" />
          <text x="31" y="20.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="Helvetica, Arial, sans-serif">Pay</text>
        </svg>
      );
    case 'google_pay':
      return (
        <svg {...common} viewBox="0 0 48 32" width={(height / 32) * 48}>
          <rect x="2" y="4" width="44" height="24" rx="6" fill="#fff" stroke="#dadce0" strokeWidth="1" />
          <circle cx="15" cy="16" r="6" fill="none" stroke="#4285f4" strokeWidth="2.6" strokeDasharray="24 14" strokeLinecap="round" />
          <path d="M15 16 h6" stroke="#4285f4" strokeWidth="2.6" />
          <text x="31" y="20" textAnchor="middle" fontSize="9" fontWeight="700" fill="#5f6368" fontFamily="Helvetica, Arial, sans-serif">Pay</text>
        </svg>
      );
    // WEB-2963: своей картинки у PayPal больше нет — это снятый способ оплаты
    // (`paymentBannerOffer.ts`, RETIRED_PAYMENT_METHODS), обе отрисовки его
    // отсеивают; лишний рисунок только утяжелял первый экран.
    default:
      // Generic-щит с галкой
      return (
        <svg {...common}>
          <ShieldPath fill="#64748b" />
          <path d="M11 16 L14.5 19.5 L21 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
  }
}

export function trustBadgeLabel(badge: string): string {
  return LABELS[badge] ?? badge;
}
